import { Entity } from '@api/api/entity-schema';
import {
  appTableFeatures,
  RevampTable,
  TableCell,
  TableHeader,
} from '@design/components/table';
import { flexRender, useTable } from '@tanstack/react-table';
import { ConnectionsEmptyOrError } from '@terminal/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import { useBrandsAndProducts } from '@terminal/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import clsx from 'clsx';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderCell } from '../../theme-exposure-tab/theme-exposure-table/loader-cell/LoaderCell';
import { RankingMethodologySection } from '../../theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import { useBrandAndProductDrawer } from '../useBrandAndProductDrawer';
import styles from './BrandsAndProductsTable.module.scss';
import { getColumns, RANK_COLUMN_ID } from './utils/getColumns';

interface BrandsAndProductsTableProps {
  entity: Entity;
}

export const BrandsAndProductsTable = ({
  entity,
}: BrandsAndProductsTableProps) => {
  const { t } = useTranslation(['widget']);

  const containerRef = useRef<HTMLDivElement>(null);
  const { data = [], error, isFetching } = useBrandsAndProducts(entity.tag);
  const { openDrawer } = useBrandAndProductDrawer({ entityTag: entity.tag });

  const isEmpty = !isFetching && data.length === 0;
  const columns = getColumns({ t });
  const table = useTable({
    features: appTableFeatures,
    data,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: Infinity },
    },
  });

  if (error && error.status !== httpCode.notFound) {
    return (
      <div className={styles.stateRoot}>
        <ConnectionsEmptyOrError variant="error" errorSize="fullView" />
        <RankingMethodologySection rankingType="companyToBrandAndProduct" />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={styles.stateRoot}>
        <ConnectionsEmptyOrError
          variant="empty"
          layout="fillContainer"
          imageSize="full"
          title={t(
            'widget:assetOverview.knowledgeGraphRevamp.empty.brandsAndProducts'
          )}
        />
        <RankingMethodologySection rankingType="companyToBrandAndProduct" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(styles.tableWrapper, {
        [styles.loading]: isFetching,
      })}
    >
      <RevampTable data-testid="brands-and-products-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <TableHeader
                    key={header.id}
                    className={styles.styledTableHeader}
                    data-table-header
                    alignment="left"
                    isHoverEnabled={false}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <LoaderCell numberOfRows={3} />
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id} onClick={() => openDrawer(row.original.product)}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <TableCell
                      key={cell.id}
                      className={styles.styledTableCell}
                      data-table-cell
                      alignment="left"
                      isNumber={cell.column.id === RANK_COLUMN_ID}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))
          )}
          <tr data-testid="footer-row">
            <TableCell
              className={styles.footerRow}
              data-footer-row
              colspan={table.getHeaderGroups()[0].headers.length}
              isNumber={false}
            >
              <RankingMethodologySection rankingType="companyToBrandAndProduct" />
            </TableCell>
          </tr>
        </tbody>
      </RevampTable>
    </div>
  );
};
